import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { getDashboardAnalytics } from "../../../Actions/DashboardActions";
import NoData from "../../../components/NoData";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SurveyAnalytics = ({ allMonthsAndYear, user }) => {
  const [surveyOverviewFilters, setSurveyOverviewFilters] = useState({
    month: new Date().getMonth(),
    year: new Date()?.getFullYear(),
  });
  const {
    data: dashboardAnalyticsData,
    isLoading: dashboardAnalyticsDataLoading,
    isError: dashboardAnalyticsDataError,
  } = useQuery({
    queryFn: () =>
      getDashboardAnalytics(
        surveyOverviewFilters?.month,
        surveyOverviewFilters?.year,
        user?.organizationId
      ),
    queryKey: [
      "dashboardAnalytics",
      surveyOverviewFilters?.month,
      surveyOverviewFilters?.year,
      user?.organizationId,
    ],
    enabled: !!user?.organizationId,
    staleTime: 10000,
    cacheTime: 15000,
  });

  const BarChart = () => {
    // Google-like chart styling
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "#fff",
          titleColor: "#202124",
          bodyColor: "#5f6368",
          borderColor: "#dadce0",
          borderWidth: 1,
          padding: 12,
          titleFont: {
            size: 13,
            family: "'Google Sans', 'Roboto', 'Arial', sans-serif",
            weight: "500",
          },
          bodyFont: {
            size: 12,
            family: "'Google Sans', 'Roboto', 'Arial', sans-serif",
          },
          cornerRadius: 8,
          boxPadding: 4,
          usePointStyle: true,
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            font: {
              size: 10,
              family: "'Google Sans', 'Roboto', 'Arial', sans-serif",
            },
            color: "#80868b",
            padding: 4,
          },
          border: {
            display: false,
          },
        },
        y: {
          stacked: true,
          grid: {
            color: "#f1f3f4",
            drawBorder: false,
          },
          ticks: {
            font: {
              size: 11,
              family: "'Google Sans', 'Roboto', 'Arial', sans-serif",
            },
            color: "#80868b",
            padding: 8,
            stepSize: 1,
          },
          border: {
            display: false,
          },
        },
      },
    };

    let obj = { label: [], data1: [], data2: [] };

    for (
      let i = 1;
      i <= Object?.keys(dashboardAnalyticsData || {}).length;
      i++
    ) {
      let suffix = "th";
      if (i % 10 === 1 && i !== 11) {
        suffix = "st";
      } else if (i % 10 === 2 && i !== 12) {
        suffix = "nd";
      } else if (i % 10 === 3 && i !== 13) {
        suffix = "rd";
      }
      obj.label.push(i + suffix);
    }

    Object?.entries(dashboardAnalyticsData || {})?.map(([key, value]) => {
      obj.data1.push(value?.paused);
      obj.data2.push(value?.live);
    });

    const data = {
      labels: obj.label,
      datasets: [
        {
          label: "Live",
          backgroundColor: "#4285F4",
          hoverBackgroundColor: "#1a73e8",
          data: obj.data2,
          barThickness: 14,
          borderRadius: 4,
          borderSkipped: false,
        },
        {
          label: "Paused",
          backgroundColor: "#FBBC04",
          hoverBackgroundColor: "#f9a825",
          data: obj.data1,
          barThickness: 14,
          borderRadius: 4,
          borderSkipped: false,
        },
      ],
    };

    return (
      <div className="w-full h-full">
        <Bar options={options} data={data} />
      </div>
    );
  };

  const handleFilterChange = (e) => {
    setSurveyOverviewFilters((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const totalLive = Object.values(dashboardAnalyticsData || {})?.reduce(
    (acc, cur) => (acc += cur?.live),
    0
  );
  const totalPaused = Object.values(dashboardAnalyticsData || {})?.reduce(
    (acc, cur) => (acc += cur?.paused),
    0
  );

  return (
    <div className="w-full bg-white border border-[#dadce0] rounded-xl h-[380px] flex flex-col overflow-hidden shadow-sm">
      <div className="px-6 py-5 border-b border-[#E0E0E0] flex items-center justify-between">
        <div>
          <h1 className="text-[#202124] text-[16px] font-[500]">
            Survey Analysis
          </h1>
          <p className="text-[13px] text-[#5f6368] mt-1">
            Daily survey creation activity
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="h-[36px] px-3 pr-8 text-[#202124] text-[13px] font-[500] bg-white border border-[#dadce0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4285F4] focus:border-[#4285F4] transition-all cursor-pointer hover:bg-[#f8f9fa] appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%235f6368%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3e%3cpolyline points=%226 9 12 15 18 9%22%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:16px] bg-[right_8px_center] bg-no-repeat"
            value={surveyOverviewFilters?.year}
            name="year"
            onChange={handleFilterChange}
          >
            <option value="">Year</option>
            {allMonthsAndYear?.years?.map((d) => (
              <option value={d} key={d}>
                {d}
              </option>
            ))}
          </select>
          <select
            className="h-[36px] px-3 pr-8 text-[#202124] text-[13px] font-[500] bg-white border border-[#dadce0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4285F4] focus:border-[#4285F4] transition-all cursor-pointer hover:bg-[#f8f9fa] appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%235f6368%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3e%3cpolyline points=%226 9 12 15 18 9%22%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:16px] bg-[right_8px_center] bg-no-repeat"
            value={surveyOverviewFilters?.month}
            name="month"
            onChange={handleFilterChange}
          >
            <option value="">Month</option>
            {allMonthsAndYear?.months?.map((d) => (
              <option value={d?.value} key={d?.value}>
                {d?.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {dashboardAnalyticsDataLoading ? (
        <div className="w-full flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[#4285F4] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-[#5f6368] text-[13px]">Loading chart...</p>
          </div>
        </div>
      ) : dashboardAnalyticsDataError ? (
        <div className="w-full flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-50 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <p className="text-[#ea4335] text-[13px] font-medium">
              Something went wrong!
            </p>
          </div>
        </div>
      ) : Object?.values(dashboardAnalyticsData || {})?.filter(
          (d) => d?.paused || d?.live
        )?.length ? (
        <>
          <div className="px-6 py-3 flex items-center gap-8 border-b border-[#f1f3f4] bg-[#fafafa]">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-sm bg-[#4285F4]"></div>
              <div>
                <span className="text-[#202124] text-[18px] font-[600]">
                  {totalLive}
                </span>
                <span className="text-[12px] text-[#5f6368] ml-2">
                  Live Surveys
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-sm bg-[#FBBC04]"></div>
              <div>
                <span className="text-[#202124] text-[18px] font-[600]">
                  {totalPaused}
                </span>
                <span className="text-[12px] text-[#5f6368] ml-2">
                  Paused Surveys
                </span>
              </div>
            </div>
          </div>
          <div className="px-6 py-4 flex-1 overflow-hidden w-full">
            <div className="w-full h-full">
              <BarChart />
            </div>
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <NoData
            msg={
              <span className="text-[#5f6368]">
                Try changing the filters to see data
              </span>
            }
          />
        </div>
      )}
    </div>
  );
};

export default SurveyAnalytics;
